// ══════════════════════════════════════════════════════════════════════
// PR-CANONICAL-EVIDENCE-A1 — pure derivation helpers over ChildAttempt.
//
// The canonical evidence contract (canonicalEvidenceContract.md, same dir)
// names how every ChildAttempt field is derived at write time and what it
// means at read time. These helpers are the executable half of that
// contract: pure, total functions over a single ChildAttempt that the
// Wave B aggregators (independent mastery, support need, retention) consume
// instead of re-deriving mixed evidence ad hoc.
//
// A1 scope = helpers + tests + contract doc. NO schema migration, NO
// session-screen wiring (A2), NO aggregator consuming these (Wave B).
// Post-A1 / pre-A2, isIndependentAttempt returns false for EVERY real
// attempt because buildAttempt does not populate `audienceMode` yet — this
// is the documented inert behavior, not a bug.
//
// Grounding citations live in canonicalEvidenceContract.md §1 (verbatim).
//
// ── FORWARD-COMPAT INVARIANT (A3.1 M1): wiring reveal-UI on a NEW screen ──
// A3.1 wired `answerRevealed` for MathSession ONLY, because it is the sole
// session screen with reveal-and-advance UI today (the other three —
// Reading/Mixed/Foundation — have no reveal affordance; SessionScreen.jsx has
// one but does not write child_attempts). If a future screen gains a
// reveal-and-advance affordance, it MUST wire `answerRevealed` predictively:
// compute the reveal decision BEFORE the recordAttempt call (so the triggering
// attempt itself carries the flag), pass it through buildAttempt's
// ItemAttemptEvent.answerRevealed, and the consumer here (attemptKindFor's
// `attempt.answerRevealed === true` branch) classifies it. Search for
// `answerRevealed` references when introducing the affordance. This is a
// discoverable invariant, not a binding contract on screens that don't exist.
// ══════════════════════════════════════════════════════════════════════

import type { ChildAttempt, ResponseChannel } from "@/adaptation/types";

// ── AttemptKind ──────────────────────────────────────────────────────
//
// Canonical per the wrong-answer prior L107's six event labels, whose
// "I would implement AT LEAST these event labels" wording is an explicit
// extension license. Pre-flight #7 confirmed retry-without-hint IS
// reachable in current PencilWise UI (MathSession clears feedback and
// re-shows the same item on a first miss), so the symmetric 7th category
// `incorrect_after_error_without_hint` is added — the failure-side sibling
// of L107's own `correct_after_error_without_hint`. See contract §4.1.
type AttemptKind =
  | "independent_correct"
  | "incorrect_first_try"
  | "correct_after_hint"
  | "correct_after_error_without_hint"
  | "incorrect_after_hint"
  | "incorrect_after_error_without_hint" // 7th — pre-flight #7 reachable; L107 "at least these" license
  | "answer_revealed"; // EMITTED (A3.1 wired the producer) — see contract §6 L1.
//                        The ChildAttempt.answerRevealed field captures the
//                        MathSession reveal-and-advance trigger (the in-place
//                        answer reveal + advance after a 2-miss streak); when
//                        true, attemptKindFor returns this value ahead of all
//                        others. U3 (W3) widened the producer set: the unified
//                        "I don't know" hatch now LOGS an attempt on every
//                        screen (errorType "i_dont_know"); on Reading/Mixed/
//                        Foundation-MC it reveals the answer before advancing,
//                        so those IDK attempts carry answerRevealed:true. Math
//                        IDK routes to the coach (no in-place reveal →
//                        answerRevealed:false). shouldRevealAnswerOnWrong stays
//                        a NON-producer: it is the legacy SessionScreen.jsx
//                        path, which does not write child_attempts at all (see
//                        A3.1 forward-compat note).

// ── Evidence contract version ────────────────────────────────────────
//
// Incremented at each A-wave migration boundary. A1 ships the constant; A2
// wires the session screens to stamp it on every new attempt (column added
// in A2's migration). Wave B aggregators filter `evidenceContractVersion >=
// EVIDENCE_CONTRACT_VERSION` so legacy rows — whose `retryIndex` /
// `isRepetition` / `audienceMode` are fromRow/undefined artifacts rather
// than real captured evidence — are excluded from mastery math. This
// replaces the unenforceable "infer migration apply-date from git" approach
// the v2 dispatch proposed. See contract §6 L9.
export const EVIDENCE_CONTRACT_VERSION = 1;

// ── isFirstAttempt ───────────────────────────────────────────────────
/**
 * True when this attempt is the session's FIRST independent re-test of the
 * item — the mastery-truth attempt (wrong-answer prior L5: "log the first
 * independent attempt as the mastery truth").
 *
 * Derivation: `retryIndex === 0 && !isRepetition`. Both fields are optional
 * on the type (round-trippability for pre-0009 rows); buildAttempt and
 * fromRow each default them (0 / false), so a present-and-defaulted value
 * reads as a first attempt. Undefined is treated as the same default here
 * (`?? 0`, `?? false`) — the conservative back-compat reading that matches
 * buildAttempt's own `?? 0`. Note L9: legacy-defaulted values are fromRow
 * artifacts, not real evidence, and Wave B excludes them by version filter.
 *
 * Keying on `isRepetition` (not raw `retryIndex` alone) is load-bearing: a
 * within-session re-serve carries `retryIndex === 0, isRepetition === true`
 * and MUST NOT be counted as a first attempt (contract §4.2).
 *
 * Within-session only (contract §6 L3): a fresh session's first attempt is
 * its own independent re-test, matching spaced-review semantics.
 */
export function isFirstAttempt(attempt: ChildAttempt): boolean {
  return (attempt.retryIndex ?? 0) === 0 && !(attempt.isRepetition ?? false);
}

// ── isAssistanceFree ─────────────────────────────────────────────────
/**
 * True when the kid answered with NO active teaching support: no hint
 * (`hintLevel === "H0"`) AND no non-independent support mode.
 *
 * Derivation: `hintLevel === "H0" && supportMode.every(m => m ===
 * "independent")`. Per Section 4 L98 the canonical unaided state is
 * `supportMode === ["independent"]`, NOT `[]` — pre-flight #3 confirmed all
 * four screens populate at least `["independent"]` (Math also emits
 * `["H1"]`/`["H2"]` when hinted; Foundation `["visualScaffolded"]` for trace
 * items). `[].every()` is vacuously true, so a defensive empty array also
 * reads assistance-free — both shapes handled. See contract §3, §4.2.
 *
 * NB SupportMode includes the hint tokens "H0".."H4" as discrete values, so
 * a hinted Math attempt's `["H1"]` is correctly NOT assistance-free here even
 * before the hintLevel check.
 */
export function isAssistanceFree(attempt: ChildAttempt): boolean {
  return (
    (attempt.hintLevel ?? "H0") === "H0" &&
    (attempt.supportMode ?? []).every((m) => m === "independent")
  );
}

// ── attemptKindFor ───────────────────────────────────────────────────
/**
 * The canonical attemptKind for an attempt — the §4.2 truth table,
 * implemented in terms of `isFirstAttempt` and `isAssistanceFree` (NOT raw
 * `retryIndex`, so a re-served `retryIndex === 0, isRepetition === true`
 * item is never mislabeled a first try).
 *
 * Two deliberate asymmetries (contract §4.1):
 *  - CORRECT + first-try: support-WITHOUT-hint counts as assisted
 *    (`correct_after_hint`, extended sense), because support may have carried
 *    a correct answer — it cannot certify independent mastery. Reachable via
 *    Foundation trace items (hintLevel "H0", supportMode ["visualScaffolded"]).
 *  - INCORRECT: keyed on hint only (`hintLevel`), not assistance-free —
 *    support present or not, a failure is still a failure; the relevant axis
 *    is whether teaching (a hint) occurred.
 *
 * Total over `ChildAttempt` — `correct` is non-nullable in the current type
 * (PencilWise scores synchronously), so there is no null branch. The async-
 * scored `correct === null` case is deferred to Wave C, which widens the
 * field AND this signature together (contract §6 L8). Returns
 * `answer_revealed` when `attempt.answerRevealed === true` (A3.1 wired the
 * producer; contract §6 L1) — that check runs FIRST and takes precedence over
 * every other state, since a revealed answer cannot certify any independent or
 * post-hint outcome.
 */
export function attemptKindFor(attempt: ChildAttempt): AttemptKind {
  // A3.1: the reveal-and-advance trigger takes precedence over every other
  // state. `=== true` so a defaulted-false or legacy-undefined attempt falls
  // through to the six original categories (contract §6 L1).
  if (attempt.answerRevealed === true) return "answer_revealed";
  const first = isFirstAttempt(attempt);
  const free = isAssistanceFree(attempt);
  const hinted = (attempt.hintLevel ?? "H0") !== "H0";

  if (attempt.correct) {
    if (first) {
      return !hinted && free ? "independent_correct" : "correct_after_hint";
    }
    return hinted ? "correct_after_hint" : "correct_after_error_without_hint";
  }
  // incorrect
  if (first) {
    return hinted ? "incorrect_after_hint" : "incorrect_first_try";
  }
  return hinted ? "incorrect_after_hint" : "incorrect_after_error_without_hint";
}

// ── isIndependentAttempt ─────────────────────────────────────────────
/**
 * True when the attempt is admissible as INDEPENDENT MASTERY evidence
 * (T1-06 aggregator input): produced in `independent_practice` evidence mode,
 * AND a first attempt, AND assistance-free.
 *
 * `audienceMode === "independent_practice"` (not `!== "scaffolded"`) so that
 * undefined (every attempt pre-A2 — buildAttempt does not set the field yet)
 * AND null AND "scaffolded"/"review_queue" all read as NOT independent. This
 * is the documented inert behavior in A1: returns false for every real
 * attempt until A2 wires the producer. See contract §3, §5 (T1-06), §6 L9.
 */
export function isIndependentAttempt(attempt: ChildAttempt): boolean {
  return (
    attempt.audienceMode === "independent_practice" &&
    isFirstAttempt(attempt) &&
    isAssistanceFree(attempt)
  );
}

// ── Rapid-guess thresholds (PROVISIONAL) ─────────────────────────────
//
// Keyed by ResponseChannel — the INTERACTION modality, which is what a
// minimum-plausible-latency floor actually depends on (how the kid responds),
// not the pedagogical ItemType. Per pre-flight #9 the dispatch's original
// keys (tap_count, multiple_choice, …) are not real ItemType values; the
// codebase's interaction-modality axis is ResponseChannel.
//
// PROVISIONAL — orchestrator/founder engineering judgment, NOT prior-derived
// (contract §6 L11). The modalities prior L363 supports only an OPTIONAL
// `targetMedianLatencyMs?` on the FluencyTimedItem schema; these per-channel
// floors are the architectural hook for Wave B telemetry calibration, not
// validated thresholds. Recalibrate from real latency distributions.
const DEFAULT_RAPID_GUESS_MS = 1000;

const RAPID_GUESS_THRESHOLDS_MS: Partial<Record<ResponseChannel, number>> = {
  tap: 500, // visible objects, single-tap — lowest plausible floor
  dragDrop: 1000, // target acquisition + drag
  manipulative: 1000, // object interaction
  oral: 1500, // speech preparation + utterance
  handwritten: 2000, // motor planning + formation
  mixed: 1000, // composite — default-equivalent
};

/** The provisional rapid-guess latency floor (ms) for a response channel. */
export function getRapidGuessThresholdMs(channel: ResponseChannel): number {
  return RAPID_GUESS_THRESHOLDS_MS[channel] ?? DEFAULT_RAPID_GUESS_MS;
}

// ── isRapidGuess ─────────────────────────────────────────────────────
/**
 * True when `latencyMs` is below the channel's plausible-response floor — a
 * likely non-engaged tap rather than a real answer (wrong-answer prior L131
 * lists "rapid-guess rate" among launch-monitoring metrics).
 *
 * Binding for fluency-mode items (modalities prior L365-367
 * `canCertifyMasteryAlone: false`); Recommended-only for non-fluency
 * modalities — the priors do not support a generic "latency is mastery-
 * material across all item types" claim (contract §6 L12). Strict `<`, so a
 * latency exactly equal to the threshold is NOT a rapid guess.
 */
export function isRapidGuess(attempt: ChildAttempt): boolean {
  return attempt.latencyMs < getRapidGuessThresholdMs(attempt.responseChannel);
}

// ── isAbandonment ────────────────────────────────────────────────────
/**
 * MA-331 — the wrong-answer prior L131 "item abandonment after error" metric.
 * `attempt` is the incorrect attempt on the abandoned item; `exitSignal` is
 * the session_exit marker's contextData for the SAME run (childId + runId +
 * itemId match confirms this is the attempt the kid actually quit on, not
 * merely the last attempt logged before an unrelated exit). Returns `false`
 * when `exitSignal` is omitted (no exit was recorded, or it belongs to a
 * different run/item) — the pre-MA-331 safe default.
 *
 * ⚠️No production call site assembles `exitSignal` yet — this slice makes the
 * derivation correct and testable (milestoneLogger.buildSessionExitEvent is
 * the producer; nothing joins its rows back to a ChildAttempt for this
 * function to consume in prod). A future aggregator wires that join; see
 * contract §6.
 */
export function isAbandonment(
  attempt: ChildAttempt,
  exitSignal?: { runId: string; itemId: string; afterError: boolean },
): boolean {
  if (!exitSignal) return false;
  return (
    attempt.correct === false &&
    exitSignal.runId === attempt.runId &&
    exitSignal.itemId === attempt.itemId &&
    exitSignal.afterError === true
  );
}
